import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { LogOut, LayoutDashboard, Mail, Wrench } from "lucide-react";
import DownloadFolderButton from "@/components/DownloadFolderButton";
import ClubSwitcher from "@/components/ClubSwitcher";
import HelpButton, { useHelpSection } from "@/components/HelpButton";
import HelpChat from "@/components/HelpChat";
import McJobNotifier from "@/components/McJobNotifier";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const { clubId } = useParams();
  const helpSection = useHelpSection();
  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b-2 border-foreground/90 bg-background sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto flex items-center gap-4 sm:gap-8 px-4 sm:px-6 py-4">
          <Link to="/" data-testid="brand-link" className="flex items-center h-10 shrink-0">
            <img
              src="/logo-meerjarenplan.png"
              alt="MeerjarenplanOnline.nl"
              className="h-8 sm:h-10 w-auto max-w-[42vw] sm:max-w-none object-contain object-left"
              data-testid="brand-logo"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-4">
            <NavLink
              to="/"
              end
              data-testid="nav-dashboard"
              className={({ isActive }) =>
                `h-10 px-3 flex items-center font-display uppercase font-bold tracking-wide text-sm ${
                  isActive ? "bg-foreground text-background" : "text-foreground hover:bg-muted"
                }`
              }
            >
              <span className="inline-flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> {t("Dashboard")}</span>
            </NavLink>
            {user?.role === "admin" && (
              <NavLink
                to="/uitnodigingen"
                data-testid="nav-invites"
                className={({ isActive }) =>
                  `h-10 px-3 flex items-center font-display uppercase font-bold tracking-wide text-sm ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "bg-foreground text-background hover:bg-foreground/90"
                  }`
                }
              >
                <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> {t("Uitnodigingen")}</span>
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink
                to="/beheer"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `h-10 px-3 flex items-center font-display uppercase font-bold tracking-wide text-sm ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "bg-foreground text-background hover:bg-foreground/90"
                  }`
                }
              >
                <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4" /> {t("Beheer")}</span>
              </NavLink>
            )}
            {user && <ClubSwitcher />}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <DownloadFolderButton compact scope={clubId} />
            {user ? (
              <Link
                to="/account"
                data-testid="header-account-link"
                className="hidden sm:flex flex-col justify-center text-right leading-tight h-10 hover:text-primary transition-colors"
              >
                <span className="stat-label">{t("Ingelogd")}</span>
                <span className="font-mono-num text-sm underline decoration-dotted underline-offset-4" data-testid="header-user-email">{user.email}</span>
              </Link>
            ) : (
              <div className="hidden sm:flex flex-col justify-center text-right leading-tight h-10">
                <span className="stat-label">{t("Anoniem bezoek")}</span>
              </div>
            )}
            {user && helpSection && <HelpButton section={helpSection} />}
            {user ? (
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="btn-brutal bg-secondary text-secondary-foreground h-10 px-3 flex items-center font-display uppercase font-bold text-sm"
              >
                <span className="inline-flex items-center gap-2"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("Uitloggen")}</span></span>
              </button>
            ) : (
              <Link
                to="/login"
                data-testid="login-cta"
                className="btn-brutal bg-primary text-primary-foreground h-10 px-3 flex items-center font-display uppercase font-bold text-sm"
              >
                {t("Inloggen")}
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">{children}</main>
      <footer className="border-t border-border mt-16 py-6 text-xs text-muted-foreground">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} MeerjarenplanOnline.nl</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground underline">
              {t("Privacyverklaring")}
            </Link>
            <Link to="/voorwaarden" data-testid="footer-terms-link" className="hover:text-foreground underline">
              {t("Algemene voorwaarden")}
            </Link>
            <a href="mailto:info@club-fin.nl" className="hover:text-foreground underline">
              info@club-fin.nl
            </a>
          </div>
        </div>
      </footer>
      <HelpChat />
      {user && <McJobNotifier />}
    </div>
  );
}
